<template>
    <AppFormValidator
        ref="validator"
        #default="{ errors }"
        :model="model"
        :rules="rules"
    >
        <CForm @submit.prevent>
            <CInput
                v-model="model.title"
                horizontal
                label="Название"
                placeholder="Введите название"
                :is-valid="errors.title && errors.title.isValid"
            />

            <CTextarea
                v-model="model.description"
                :is-valid="errors.description && errors.description.isValid"
                :rows="5"
                horizontal
                label="Краткое описание"
                placeholder="Укажите краткое описание (не более 255 символов)"
            />

            <CInput
                v-model="model.price"
                :is-valid="errors.price && errors.price.isValid"
                type="number"
                horizontal
                label="Цена"
            />

            <CInput
                v-model="model.position"
                :is-valid="errors.position && errors.position.isValid"
                horizontal
                label="Позиция"
            />

            <div class="flex items-center mt-2">
                <div class="w-56 col-form-label">Популярный</div>
                <CSwitch
                    class="ml-5"
                    color="success"
                    shape="pill"
                    variant="opposite"
                    :checked.sync="model.popular"
                />
            </div>

            <div class="flex items-center mt-2">
                <div class="w-56 col-form-label">Активен</div>
                <CSwitch
                    class="ml-5"
                    color="success"
                    shape="pill"
                    variant="opposite"
                    :checked.sync="model.enabled"
                />
            </div>

            <div id="product-picture" class="flex items-start mt-2">
                <div class="w-1/4 col-form-label">Картинки</div>
                <div class="flex-1">
                    <CInputFile
                        custom
                        placeholder="Выберите изображение"
                        :is-valid="errors.pictures && errors.pictures.isValid"
                        @change="uploadPicture"
                    />

                    <div class="relative flex flex-wrap w-full mt-2 -ml-2">
                        <div
                            v-for="item in model.pictures"
                            :key="item"
                            class="w-1/4 p-2">
                            <div
                                class="relative w-full overflow-hidden bg-no-repeat bg-cover rounded-md shadow-md group-hover"
                                :style="{
                                    paddingTop: '100%',
                                    backgroundImage: `url(${getStaticPath(item)})`,
                                    backgroundPosition: 'center center',
                                }"
                            >
                                <button
                                    class="absolute top-0 right-0 w-8 h-8 text-xl text-white bg-red-400 rounded-bl-md"
                                    @click="removePicture(item)"
                                >
                                    <CIcon :height="16" :content="cilTrash" />
                                </button>

                                <button
                                    class="absolute bottom-0 flex items-center w-full h-10 px-1 bg-black bg-opacity-75"
                                    @click="toggleMainPicture(item)"
                                >
                                    <div
                                        v-if="model.main_picture === item"
                                        class="flex items-center text-green-500"
                                    >
                                        <CIcon :height="20" :content="cilCheckCircle" />
                                        <span class="ml-2 text-xs">Основная картинка</span>
                                    </div>
                                    <div v-else class="flex items-center text-white">
                                        <CIcon :height="20" :content="cilCircle" />
                                        <span class="ml-2 text-xs">Выбрать как основную</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CForm>

        <div class="flex mt-4">
            <CButton
                color="primary"
                @click="submit"
            >{{ submitButtonTitle }}
            </CButton>
            <CButton
                v-if="model.id"
                variant="outline"
                color="danger"
                class="ml-4"
                @click="promptDelete"
            >Удалить
            </CButton>
        </div>

        <AppConfirmDialog
            ref="confirmDialog"
            title="Подтверждение"
            color="danger"
            text="Вы действительно хотите удалить запись? Это действие нельзя будет отменить."
            @confirm="confirmDelete"
        />
    </AppFormValidator>
</template>

<script>
    import to from 'await-to-js';
    import { morphism } from 'morphism';
    import { requiredRule } from '@/utils/rules';
    import { focusProductPicture } from '@/utils/focusPicture';
    import { cilTrash, cilCheckCircle, cilCircle } from '@coreui/icons';
    import AppFormValidator from '@/components/AppFormValidator';
    import AppConfirmDialog from '@/components/AppConfirmDialog';

    export default {
        components: {
            AppFormValidator,
            AppConfirmDialog,
        },

        props: {
            entity: {
                type: Object,
                default: () => ({}),
            },
        },

        data() {
            return {
                cilTrash,
                cilCircle,
                cilCheckCircle,
                model: {},
                schema: {
                    id: 'id',
                    title: 'title',
                    description: 'description',
                    main_picture: 'main_picture',
                    price: 'price',
                    pharmacy_id: () => this.$route.params.id,
                    position: source => (parseInt(source.position) ? source.position : 1),
                    pictures: source =>
                        Array.isArray(source.pictures) ? source.pictures : [],
                    popular: source => Boolean(source.popular),
                    enabled: source => Boolean(source.enabled),
                },
                rules: {
                    title: [requiredRule],
                    description: [requiredRule],
                    main_picture: [requiredRule],
                    pictures: [requiredRule],
                    popular: [requiredRule],
                    position: [requiredRule],
                    enabled: [requiredRule],
                    price: [requiredRule],
                },
            };
        },

        computed: {
            submitButtonTitle() {
                return typeof this.entity.id === 'number' ? 'Сохранить' : 'Создать';
            },
        },

        created() {
            this.setModel();
        },

        mounted() {
            focusProductPicture(this.$route);
        },

        methods: {
            setModel() {
                this.model = morphism(this.schema, this.entity);
            },

            async uploadPicture(FileList) {
                const form = new FormData();
                form.append('file', FileList[0]);

                const [err, result] = await to(this.axios.post('/files/upload', form));

                if (err) {
                    this.$root.$emit('showError', 'Ошибка. Не удалось загрузить картинку.');
                }

                this.model.pictures.push(result.data.filename);

                if (this.model.pictures.length === 1) {
                    this.toggleMainPicture(result.data.filename);
                }
            },

            async submit() {
                const [error] = await to(this.$refs.validator.validate());

                if (error) {
                    return false;
                }

                const pharmacy_id = this.$route.params.id;
                const { id, ...payload } = this.model;

                payload.position = parseInt(payload.position);
                payload.price = parseFloat(payload.price);

                if (typeof id === 'number') {
                    const [err] = await to(
                        this.axios.put(`/pharmacies/${pharmacy_id}/drugs/${id}`, payload)
                    );
                    if (err) {
                        return this.$root.$emit('showError', 'Ошибка. Не удалось сохранить запись.');
                    }
                } else {
                    const [err] = await to(
                        this.axios.post(`/pharmacies/${pharmacy_id}/drugs`, payload)
                    );
                    if (err) {
                        return this.$root.$emit('showError', 'Ошибка. Не удалось создать новую запись.');
                    }
                }

                this.$emit('success');
            },

            promptDelete() {
                this.$refs.confirmDialog.open();
            },

            async confirmDelete() {
                if (!this.model.id) {
                    return false;
                }

                const [error] = await to(
                    this.axios.delete(
                        `/pharmacies/${this.model.pharmacy_id}/drugs/${this.model.id}`
                    )
                );

                if (error) {
                    return this.$root.$emit('showError', 'Ошибка при удалении записи. ' + error);
                }

                this.$emit('deleted');
            },

            toggleMainPicture(url) {
                if (this.model.main_picture === url) {
                    return false;
                }

                this.model.main_picture = url;
            },

            removePicture(url) {
                const idx = this.model.pictures.indexOf(url);

                if (idx !== -1) {
                    this.model.pictures.splice(idx, 1);
                }
            },

            getStaticPath(filename = '') {
                if (filename.startsWith('http')) {
                    return filename;
                }

                return `${process.env.VUE_APP_STATIC_BASE_URL}/${filename}`;
            },
        },
    };
</script>
